/* Curated terminal + lounge editorial for the busiest Indian airports.
   Hand-authored (Feb 2026) with official source links and last-verified dates.
   Photos: Unsplash (direct image URLs).
   Flipping LLM backfill back on will NOT overwrite this — these records are
   editorial-first and keyed by IATA.

   `zone` on lounges is used by the Lounge Locator pin-board map on
   /airports/:slug/lounges — a lightweight schematic that places each lounge
   inside a labelled terminal zone. Keep zone short (A/B/C/Pier East, etc.) */

import { EXTRA_GUIDES, EXTRA_LOUNGE_ZONES } from "./airportGuidesExtra";

const BASE_GUIDES = {
  DEL: {
    iata: "DEL",
    name: "Indira Gandhi International Airport",
    city: "New Delhi",
    state: "Delhi",
    slug: "new-delhi-del",
    operator: "Delhi International Airport Ltd. (DIAL · GMR Group)",
    last_verified: "2026-01-15",
    sources: [
      { label: "Delhi Airport (DIAL)", url: "https://www.newdelhiairport.in/" },
      { label: "AAI", url: "https://www.aai.aero/" },
    ],
    hero: "https://images.unsplash.com/photo-1548013146-72479768bada?auto=format&fit=crop&w=2200&q=80",
    terminals: [
      {
        code: "T1",
        name: "Terminal 1",
        type: "Domestic (low-cost)",
        airlines: ["IndiGo", "SpiceJet (select)", "Akasa Air (select)"],
        description:
          "Dedicated to low-cost domestic carriers. Reopened in Aug 2024 after a long rebuild; expanded departures hall, 72 check-in counters, 20 self-bag-drops and a new multi-level car park.",
        gates: 20,
        inter_terminal:
          "No airside connection to T2/T3 — allow at least 90 minutes between a T1 arrival and a T2/T3 departure via the complimentary inter-terminal shuttle.",
        tips: [
          "Allow 2.5 hours before departure during peak hours (06:00–09:00, 18:00–21:00).",
          "Metro Airport Line does NOT serve T1 — alight at Dwarka Sector 21 or use the inter-terminal shuttle from Aerocity.",
        ],
      },
      {
        code: "T2",
        name: "Terminal 2",
        type: "Domestic",
        airlines: ["IndiGo (overflow)", "Alliance Air"],
        description:
          "Overflow domestic terminal taken over by IndiGo during peak hours. Compact, two-level layout with 12 boarding gates and a smaller food court.",
        gates: 12,
        inter_terminal: "Shuttle to T3 is complimentary and runs every 20 minutes.",
        tips: [
          "Pickup is on the ground level — the first floor is for drop-off only.",
          "Confirm your terminal 24 hours before departure; IndiGo frequently reassigns flights between T1 and T2.",
        ],
      },
      {
        code: "T3",
        name: "Terminal 3",
        type: "International + full-service domestic",
        airlines: ["Air India", "Vistara", "Delta", "Lufthansa", "Emirates", "Qatar Airways", "Singapore Airlines", "United", "Air France-KLM", "British Airways"],
        description:
          "One of the world's largest integrated terminals (168 check-in counters, 92 immigration counters, 78 aerobridges). Served by the Airport Express Metro — platform directly beneath the terminal.",
        gates: 78,
        inter_terminal:
          "International-to-international transit without immigration via a dedicated transit lounge on the Departures level.",
        tips: [
          "For a domestic→international same-ticket transfer, baggage is through-tagged; collect only at your final destination.",
          "Minimum connecting time: 1h30 for international-to-international, 2h for domestic-to-international.",
        ],
      },
    ],
    lounges: [
      {
        name: "ITC Green Lounge",
        terminal: "T3",
        location: "International Departures, Level 3",
        type: "Pay-in / Priority Pass / DreamFolks / bank cards",
        hours: "24/7",
        amenities: ["Buffet", "Showers", "Nap pods", "Full bar", "Wi-Fi"],
        access_cards: ["Priority Pass", "DreamFolks", "Axis Burgundy Private", "HDFC Infinia", "ICICI Emeralde"],
        tagline: "Spacious ITC-run lounge with a strong buffet and quiet nap zone.",
      },
      {
        name: "Encalm Prive — First Class Lounge",
        terminal: "T3",
        location: "International Departures (post-security)",
        type: "Pay-in / Centurion / select Visa Infinite",
        hours: "24/7",
        amenities: ["À la carte dining", "Spa", "Private suites", "Showers"],
        access_cards: ["Amex Platinum", "Centurion", "Priority Pass (select)"],
        tagline: "Flagship pay-in lounge — closest thing to an Emirates First experience outside Dubai.",
      },
      {
        name: "Air India Maharajah Lounge",
        terminal: "T3",
        location: "International Departures (post-security)",
        type: "Airline (Star Alliance)",
        hours: "24/7",
        amenities: ["Buffet", "Showers", "Bar"],
        access_cards: ["Air India First/Business", "Star Alliance Gold"],
        tagline: "Refurbished in 2025; expanded dining and a dedicated quiet room.",
      },
      {
        name: "Plaza Premium Lounge — Domestic",
        terminal: "T3",
        location: "Domestic Departures (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Showers", "Family room", "Work pods"],
        access_cards: ["Priority Pass", "DreamFolks", "Most Indian credit cards"],
        tagline: "The default domestic lounge — big, busy, decent food.",
      },
      {
        name: "GoSleep Pods",
        terminal: "T3",
        location: "International Transit",
        type: "Pay-in (hourly)",
        hours: "24/7",
        amenities: ["Sleep pods", "Showers (separate)"],
        access_cards: ["Walk-in (hourly rate ~ ₹1,100 for 3 hours)"],
        tagline: "Private sleep pods with privacy screens — book at a terminal kiosk.",
      },
    ],
  },

  BOM: {
    iata: "BOM",
    name: "Chhatrapati Shivaji Maharaj International Airport",
    city: "Mumbai",
    state: "Maharashtra",
    slug: "mumbai-bom",
    operator: "Mumbai International Airport Ltd. (MIAL · Adani Airports)",
    last_verified: "2026-01-15",
    sources: [
      { label: "Mumbai Airport (CSMIA)", url: "https://csmia.adaniairports.com/" },
      { label: "AAI", url: "https://www.aai.aero/" },
    ],
    hero: "https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=2200&q=80",
    terminals: [
      {
        code: "T1",
        name: "Terminal 1 (Santacruz)",
        type: "Domestic (low-cost)",
        airlines: ["IndiGo (select)", "SpiceJet", "Akasa Air", "Star Air"],
        description:
          "Legacy domestic terminal in Santacruz, 4 km road from T2. Underwent a 2024–2026 modernisation; expected to be partially replaced by the upcoming Navi Mumbai Airport (NMIA).",
        gates: 20,
        inter_terminal:
          "Complimentary inter-terminal shuttle runs every 20 minutes between T1 and T2 — allow 60 minutes.",
        tips: [
          "Entry gates and lanes get congested at 05:30–08:00; carry a printed/mobile boarding pass before you reach the terminal.",
          "Metro Line 3 (SEEPZ–Cuffe Parade) has a station at CSMIA T1 — fully operational since 2025.",
        ],
      },
      {
        code: "T2",
        name: "Terminal 2 (Sahar)",
        type: "International + full-service domestic",
        airlines: ["Air India", "Vistara", "Emirates", "Lufthansa", "Qatar Airways", "Etihad", "Singapore Airlines", "British Airways", "Cathay Pacific"],
        description:
          "Integrated international + full-service domestic terminal — one of India's architectural landmarks (Jaya He museum walk). 192 check-in counters, 60 aerobridges.",
        gates: 60,
        inter_terminal:
          "Domestic → international same-ticket transfers: baggage through-tagged; use the Transfer Desk after disembarking.",
        tips: [
          "Minimum connecting time: 2 hours (domestic → international), 1h30 (international → international).",
          "T2 Level 4 has the quietest security lanes before 10:00; the main hall gets packed in the evening international wave.",
        ],
      },
    ],
    lounges: [
      {
        name: "Adani Lounge Premium",
        terminal: "T2",
        location: "International Departures, Level 4",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet + à la carte", "Showers", "Bar", "Sleep rooms"],
        access_cards: ["Priority Pass", "DreamFolks", "Amex Platinum"],
        tagline: "The new flagship — expanded in 2025 with a dedicated sleep wing.",
      },
      {
        name: "Air India Maharaja Lounge",
        terminal: "T2",
        location: "International Departures (post-security)",
        type: "Airline (Star Alliance)",
        hours: "24/7",
        amenities: ["Buffet", "Bar", "Showers"],
        access_cards: ["Air India First/Business", "Star Alliance Gold"],
        tagline: "Refurbished 2025; quieter than the Priority Pass options next door.",
      },
      {
        name: "Emirates Lounge",
        terminal: "T2",
        location: "International Departures",
        type: "Airline",
        hours: "Open with EK flights",
        amenities: ["À la carte", "Bar", "Showers"],
        access_cards: ["Emirates First/Business", "Skywards Platinum/Gold"],
        tagline: "One of the most consistent airline lounges in India.",
      },
      {
        name: "Plaza Premium Lounge — Domestic",
        terminal: "T2",
        location: "Domestic Departures (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Family zone", "Showers"],
        access_cards: ["Priority Pass", "DreamFolks", "Most Indian credit cards"],
        tagline: "Reliable domestic option — gets busy during the 18:00–21:00 wave.",
      },
      {
        name: "GVK Lounge (T1)",
        terminal: "T1",
        location: "Domestic Departures (post-security)",
        type: "Pay-in / DreamFolks",
        hours: "04:00–23:30",
        amenities: ["Buffet", "Showers", "Nap area"],
        access_cards: ["DreamFolks", "Most Indian credit cards"],
        tagline: "Compact lounge in the legacy terminal — fine for a 60–90 minute wait.",
      },
    ],
  },

  BLR: {
    iata: "BLR",
    name: "Kempegowda International Airport",
    city: "Bengaluru",
    state: "Karnataka",
    slug: "bengaluru-blr",
    operator: "Bangalore International Airport Ltd. (BIAL · Fairfax, Siemens, AAI)",
    last_verified: "2026-01-15",
    sources: [
      { label: "BLR Airport (BIAL)", url: "https://www.bengaluruairport.com/" },
    ],
    hero: "https://images.unsplash.com/photo-1583416750470-965b2707b355?auto=format&fit=crop&w=2200&q=80",
    terminals: [
      {
        code: "T1",
        name: "Terminal 1",
        type: "Domestic only",
        airlines: ["IndiGo", "Akasa Air", "SpiceJet", "Star Air"],
        description:
          "Original 2008 terminal, now exclusively domestic. Refreshed in 2024 with a new dining court and 10 extra self-service kiosks.",
        gates: 32,
        inter_terminal:
          "Walk between T1 and T2 is a covered 400 m landscaped promenade — allow 10–12 minutes.",
        tips: ["Pickup zone is Level 1; drop-off is Level 2 — do not confuse the two during traffic."],
      },
      {
        code: "T2",
        name: "Terminal 2 — The Terminal in a Garden",
        type: "International + full-service domestic",
        airlines: ["Air India", "Vistara", "Lufthansa", "Emirates", "Singapore Airlines", "KLM", "British Airways", "Qatar Airways", "AirAsia"],
        description:
          "Opened Phase 1 Nov 2022, Phase 2 (international) in 2023. Modelled around biophilic design — hanging gardens, bamboo architecture, 60+ artworks commissioned from Indian artists.",
        gates: 95,
        inter_terminal:
          "International transit uses dedicated transit lanes on Level 3; no need to clear immigration if staying airside.",
        tips: [
          "T2 is huge — allow 20 minutes to walk from security to the farthest gates (G21–G28).",
          "Metro Phase 2B (airport link) is still under construction — plan for a taxi or Vayu Vajra airport bus (BMTC).",
        ],
      },
    ],
    lounges: [
      {
        name: "080 International Lounge",
        terminal: "T2",
        location: "International Departures (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks / airline-contract",
        hours: "24/7",
        amenities: ["South Indian buffet", "Full bar", "Spa (pay-in)", "Showers", "Nap pods"],
        access_cards: ["Priority Pass", "DreamFolks", "Amex Platinum", "HDFC Infinia"],
        tagline: "A signature Bengaluru experience — filter coffee bar, biryani station, Baobab tree atrium.",
      },
      {
        name: "080 Domestic Lounge (T2)",
        terminal: "T2",
        location: "Domestic Departures (post-security)",
        type: "Pay-in / DreamFolks / Priority Pass",
        hours: "24/7",
        amenities: ["Buffet", "Showers", "Work pods"],
        access_cards: ["Priority Pass", "DreamFolks", "Most Indian credit cards"],
        tagline: "Signature biophilic design extends to the domestic side — one of India's most beautiful lounges.",
      },
      {
        name: "Plaza Premium Lounge (T1)",
        terminal: "T1",
        location: "Domestic Departures (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Family zone", "Showers"],
        access_cards: ["Priority Pass", "DreamFolks"],
        tagline: "Classic Plaza Premium — the fastest option if you're already at T1.",
      },
      {
        name: "KLM Crown Lounge",
        terminal: "T2",
        location: "International Departures",
        type: "Airline (SkyTeam)",
        hours: "Open with KL/AF/DL flights",
        amenities: ["À la carte", "Bar", "Showers"],
        access_cards: ["SkyTeam Elite Plus", "KL/AF Business"],
        tagline: "Shared SkyTeam lounge — most consistent for KL/AF premium flyers.",
      },
    ],
  },

  HYD: {
    iata: "HYD",
    name: "Rajiv Gandhi International Airport",
    city: "Hyderabad",
    state: "Telangana",
    slug: "hyderabad-hyd",
    operator: "GMR Hyderabad International Airport Ltd.",
    last_verified: "2026-01-15",
    sources: [
      { label: "GMR Hyderabad Airport", url: "https://www.hyderabad.aero/" },
    ],
    hero: "https://images.unsplash.com/photo-1583416750470-965b2707b355?auto=format&fit=crop&w=2200&q=80",
    terminals: [
      {
        code: "T1",
        name: "Terminal 1 (integrated)",
        type: "Integrated — international + domestic",
        airlines: ["Air India", "IndiGo", "Vistara", "Lufthansa", "Emirates", "Qatar Airways", "British Airways", "Air Canada"],
        description:
          "Single integrated terminal with international on the eastern pier and domestic on the western pier. 2024–2026 expansion added 15 contact gates and a second security hall. Capacity target 34 MPPA by 2026.",
        gates: 55,
        inter_terminal:
          "Transfers happen airside — walk from the domestic pier to the international pier takes ~12 minutes.",
        tips: [
          "Pickup is on the ground level; keep your parking ticket — paid stay is strictly timed.",
          "The Airport Express Metro to Rajiv Gandhi International is scheduled to open in 2027; until then, use ORRCA road or airport taxi.",
        ],
      },
    ],
    lounges: [
      {
        name: "Encalm Lounge — International",
        terminal: "T1",
        location: "International Departures (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Showers", "Family zone"],
        access_cards: ["Priority Pass", "DreamFolks", "Amex Platinum"],
        tagline: "Reliable Priority Pass default; views over the apron.",
      },
      {
        name: "GMR Premium Lounge",
        terminal: "T1",
        location: "Domestic Departures (post-security)",
        type: "Pay-in / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Showers"],
        access_cards: ["DreamFolks", "Most Indian credit cards"],
        tagline: "Modern domestic lounge — gets busy in the evening wave.",
      },
      {
        name: "Air India Maharajah Lounge",
        terminal: "T1",
        location: "International Departures",
        type: "Airline (Star Alliance)",
        hours: "Open with AI international departures",
        amenities: ["Buffet", "Bar", "Showers"],
        access_cards: ["Air India First/Business", "Star Alliance Gold"],
        tagline: "Quieter than the pay-in lounges; refurbished in 2025.",
      },
    ],
  },

  MAA: {
    iata: "MAA",
    name: "Chennai International Airport",
    city: "Chennai",
    state: "Tamil Nadu",
    slug: "chennai-maa",
    operator: "Airports Authority of India (AAI)",
    last_verified: "2026-01-15",
    sources: [
      { label: "AAI Chennai", url: "https://www.aai.aero/en/airports/chennai" },
    ],
    hero: "https://images.unsplash.com/photo-1518638150340-f706e86654de?auto=format&fit=crop&w=2200&q=80",
    terminals: [
      {
        code: "T1",
        name: "Terminal 1",
        type: "Domestic",
        airlines: ["Air India", "IndiGo", "Vistara", "SpiceJet", "Akasa Air"],
        description:
          "Primary domestic terminal, operated by AAI. Shares landside with T4 (also domestic for low-cost operations).",
        gates: 30,
        inter_terminal:
          "T1 ↔ T4 is a short 5-minute landside walk; T1 ↔ T2 (international) is a 10-minute airside walk via the new connector built in 2023.",
        tips: ["Chennai Metro Blue Line (Airport station) is a short, flat 400 m walk from T1."],
      },
      {
        code: "T2",
        name: "Terminal 2",
        type: "International",
        airlines: ["Emirates", "Singapore Airlines", "Qatar Airways", "Etihad", "AirAsia", "Air India", "SriLankan Airlines", "Oman Air"],
        description:
          "Fully modernised in 2023 with a new integrated international building — 40 immigration counters, 18 e-gates for Indian/OCI passport holders.",
        gates: 24,
        inter_terminal: "Through-check: if your domestic + international legs are on the same PNR, your bag will be tagged to the final destination.",
        tips: [
          "Minimum connecting time: 2h for domestic → international (airside transit via the T1↔T2 connector).",
          "During monsoon (Oct–Dec), T2 Level 2 can see localised ponding near the north entrance — use the central entrance.",
        ],
      },
      {
        code: "T4",
        name: "Terminal 4",
        type: "Domestic (low-cost)",
        airlines: ["IndiGo (overflow)", "SpiceJet (overflow)"],
        description: "Compact overflow domestic terminal used for peak-hour low-cost departures.",
        gates: 8,
        inter_terminal: "5-minute landside walk to T1 — check your boarding pass carefully.",
        tips: ["Entry is via Gate 4 only — do not go through the T1 security hall."],
      },
    ],
    lounges: [
      {
        name: "Travel Club Lounge — International",
        terminal: "T2",
        location: "International Departures (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Showers", "Family zone"],
        access_cards: ["Priority Pass", "DreamFolks"],
        tagline: "The default international lounge — accepts most credit-card lounge programmes.",
      },
      {
        name: "Encalm Lounge — Domestic",
        terminal: "T1",
        location: "Domestic Departures (post-security)",
        type: "Pay-in / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Showers"],
        access_cards: ["DreamFolks", "Most Indian credit cards"],
        tagline: "Clean, functional domestic option opposite Gate 15.",
      },
      {
        name: "Air India Maharajah Lounge",
        terminal: "T2",
        location: "International Departures",
        type: "Airline (Star Alliance)",
        hours: "Open with AI international departures",
        amenities: ["Buffet", "Bar", "Showers"],
        access_cards: ["Air India First/Business", "Star Alliance Gold"],
        tagline: "The quietest option at MAA T2 — great for a quick nap before a long-haul.",
      },
    ],
  },

  COK: {
    iata: "COK",
    name: "Cochin International Airport",
    city: "Kochi",
    state: "Kerala",
    slug: "kochi-cok",
    operator: "Cochin International Airport Ltd. (CIAL)",
    last_verified: "2026-01-15",
    sources: [
      { label: "Cochin Airport (CIAL)", url: "https://cial.aero/" },
    ],
    hero: "https://images.unsplash.com/photo-1587474260584-136574528ed5?auto=format&fit=crop&w=2200&q=80",
    terminals: [
      {
        code: "T1",
        name: "Terminal 1",
        type: "Domestic",
        airlines: ["IndiGo", "Air India", "Vistara", "SpiceJet", "Akasa Air", "Alliance Air"],
        description:
          "Dedicated domestic terminal — fully solar powered since 2015 (CIAL was the world's first fully solar-powered airport).",
        gates: 20,
        inter_terminal: "T1 ↔ T3 (international) is a 400 m covered walkway — allow 8 minutes.",
        tips: ["Water Metro to central Kochi (Ernakulam) is a short road transfer away via CIAL shuttle to Pizhala jetty."],
      },
      {
        code: "T3",
        name: "Terminal 3",
        type: "International",
        airlines: ["Emirates", "Qatar Airways", "Etihad", "Air India", "Air India Express", "SriLankan Airlines", "Oman Air", "Saudia", "flydubai", "Gulf Air"],
        description:
          "Opened in 2017 as India's largest airport terminal by floor area built by a single operator — reflects Kerala's traditional architecture.",
        gates: 20,
        inter_terminal: "Immigration e-Gates (ICA SMART) rolled out in 2025 for Indian/OCI passport holders.",
        tips: [
          "Minimum connecting time: 2h (domestic → international), 1h30 (international → international).",
          "The CIAL golf course on the airport grounds offers 9-hole stays for layovers of 6+ hours.",
        ],
      },
    ],
    lounges: [
      {
        name: "CIAL Business Lounge — International",
        terminal: "T3",
        location: "International Departures (post-security)",
        type: "Pay-in / Priority Pass / DreamFolks",
        hours: "24/7",
        amenities: ["Kerala + continental buffet", "Showers", "Massage chairs"],
        access_cards: ["Priority Pass", "DreamFolks", "Most Indian credit cards"],
        tagline: "A standout at COK — authentic Kerala food, wooden interiors, calm atmosphere.",
      },
      {
        name: "CIAL Domestic Lounge",
        terminal: "T1",
        location: "Domestic Departures (post-security)",
        type: "Pay-in / DreamFolks",
        hours: "24/7",
        amenities: ["Buffet", "Showers"],
        access_cards: ["DreamFolks", "Most Indian credit cards"],
        tagline: "Fine for a 60–90 minute wait before a domestic connection.",
      },
      {
        name: "Emirates Lounge",
        terminal: "T3",
        location: "International Departures",
        type: "Airline",
        hours: "Open with EK flights",
        amenities: ["Hot meals", "Bar", "Showers"],
        access_cards: ["Emirates First/Business", "Skywards Platinum/Gold"],
        tagline: "Reliable, under-crowded, with excellent South Indian breakfast on morning EK flights.",
      },
    ],
  },
};

/* Merged exports — base editorial + extras. Keep this immediately after the
   base object so helpers below can reference it. */
export const AIRPORT_GUIDES = { ...BASE_GUIDES, ...EXTRA_GUIDES };

export function getAirportGuide(slugOrCode) {
  if (!slugOrCode) return null;
  const key = String(slugOrCode).toUpperCase();
  if (AIRPORT_GUIDES[key]) return AIRPORT_GUIDES[key];
  // slug lookup
  const needle = String(slugOrCode).toLowerCase();
  return Object.values(AIRPORT_GUIDES).find((g) => g.slug === needle) || null;
}

/* Lounge → zone map for the Lounge Locator pin-board.
   Zones are schematic (A/B/C/Pier East, etc.) — co-ordinates [x%, y%] inside
   a 100×100 unit rectangle for each terminal. The TerminalZoneMap component
   turns these into clickable pins. Keys are IATA|terminal|lounge name. */
const BASE_LOUNGE_ZONES = {
  // DEL T3
  "DEL|T3|ITC Green Lounge": { zone: "North Pier", x: 20, y: 32 },
  "DEL|T3|Encalm Prive — First Class Lounge": { zone: "Central Mezzanine", x: 52, y: 28 },
  "DEL|T3|Air India Maharajah Lounge": { zone: "South Pier", x: 78, y: 36 },
  "DEL|T3|Plaza Premium Lounge — Domestic": { zone: "Domestic Pier", x: 45, y: 70 },
  "DEL|T3|GoSleep Pods": { zone: "Transit Lounge", x: 60, y: 50 },
  // BOM T2
  "BOM|T2|Adani Lounge Premium": { zone: "Level 4 · Centre", x: 50, y: 30 },
  "BOM|T2|Air India Maharaja Lounge": { zone: "Level 4 · North", x: 25, y: 28 },
  "BOM|T2|Emirates Lounge": { zone: "Level 4 · South", x: 75, y: 32 },
  "BOM|T2|Plaza Premium Lounge — Domestic": { zone: "Domestic Pier", x: 50, y: 70 },
  // BOM T1
  "BOM|T1|GVK Lounge (T1)": { zone: "Domestic Level 1", x: 50, y: 50 },
  // BLR T2
  "BLR|T2|080 International Lounge": { zone: "International Pier", x: 70, y: 32 },
  "BLR|T2|080 Domestic Lounge (T2)": { zone: "Domestic Pier", x: 30, y: 60 },
  "BLR|T2|KLM Crown Lounge": { zone: "International Pier", x: 70, y: 55 },
  // BLR T1
  "BLR|T1|Plaza Premium Lounge (T1)": { zone: "Central", x: 50, y: 50 },
  // HYD T1
  "HYD|T1|Encalm Lounge — International": { zone: "International Pier", x: 68, y: 40 },
  "HYD|T1|GMR Premium Lounge": { zone: "Domestic Pier", x: 32, y: 55 },
  "HYD|T1|Air India Maharajah Lounge": { zone: "International Pier", x: 68, y: 60 },
  // MAA T2
  "MAA|T2|Travel Club Lounge — International": { zone: "International Departures", x: 50, y: 38 },
  "MAA|T2|Air India Maharajah Lounge": { zone: "International Departures", x: 70, y: 50 },
  // MAA T1
  "MAA|T1|Encalm Lounge — Domestic": { zone: "Domestic Departures", x: 50, y: 50 },
  // COK T3
  "COK|T3|CIAL Business Lounge — International": { zone: "International Departures", x: 50, y: 35 },
  "COK|T3|Emirates Lounge": { zone: "International Departures", x: 72, y: 50 },
  // COK T1
  "COK|T1|CIAL Domestic Lounge": { zone: "Domestic Departures", x: 50, y: 50 },
};

export const LOUNGE_ZONES = { ...BASE_LOUNGE_ZONES, ...EXTRA_LOUNGE_ZONES };

export function getLoungeZone(iata, terminal, name) {
  return LOUNGE_ZONES[`${iata}|${terminal}|${name}`] || null;
}

export const GUIDE_IATA_CODES = Object.keys(AIRPORT_GUIDES);
